import Modal from "@via/components/modal";
import { errorMessage } from "@via/pages/projects/metadata/configuration/data-import/data-import.api.tsx";
import type { VmetaAdminImportLogin } from "common";
import { type FC, useState } from "react";
import { useMergeImportLogins } from "./import-logins.api.tsx";

/**
 * Labels one "which stored value to keep" choice. Rows in a group often share a name and an owner, so the
 * sign in id and its use count are what tell the choices apart.
 */
const keepLabel = (r: VmetaAdminImportLogin): string =>
  `Keep the ${r.platform === "freezerpro" ? "password" : "key"} of ${r.name}, sign in ${r.id}, used by ${r.usedBy} (${r.ownerName ?? r.ownerEmail ?? "unknown owner"})`;

/**
 * Moves a group of matching sign ins into one organization sign in. A new one keeps the stored login of the
 * row the admin picks; the server copies it without opening it. An existing organization row in the group can be
 * the target instead. The caller keys this component by the group so state never leaks between groups.
 */
const MergeModal: FC<{
  group: VmetaAdminImportLogin[] | null;
  organizations: { id: string; name: string }[];
  onClose: () => void;
}> = ({ group, organizations, onClose }) => {
  const merge = useMergeImportLogins();
  const existing = (group ?? []).filter(r => r.scope === "organization");
  const [targetLoginId, setTargetLoginId] = useState<number | null>(null);
  const [organizationId, setOrganizationId] = useState("");
  const [name, setName] = useState("");
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const sources = (group ?? []).filter(r => r.id !== targetLoginId);
  const template = templateId ?? sources[0]?.id ?? null;

  const onMove = async (): Promise<boolean> => {
    setRefusal(null);
    if (template === null) return false;
    try {
      const result = await merge.mutateAsync({
        sourceIds: sources.map(r => r.id),
        templateId: template,
        target:
          targetLoginId !== null
            ? { loginId: targetLoginId }
            : { organizationId, name },
      });
      setDone(
        `${result.configsRebound} imports moved to sign in ${result.targetId}. ${result.sourcesDeleted.length} old sign ins removed.`
      );
      return false;
    } catch (err) {
      setRefusal(errorMessage(err, "The sign ins could not be moved."));
      return false;
    }
  };

  const field = "border rounded px-2 py-1";
  return (
    <Modal
      open={group !== null}
      setOpen={onClose}
      title="Move to organization"
      confirmText="Move"
      isConfirmLoading={merge.isPending}
      isConfirmDisabled={
        done !== null ||
        (targetLoginId === null && (!organizationId || !name.trim()))
      }
      onConfirm={onMove}>
      <div className="flex flex-col gap-3 text-sm">
        {existing.length > 0 && (
          <div className="flex flex-col">
            <label htmlFor="merge-into">Into</label>
            <select
              id="merge-into"
              className={field}
              value={targetLoginId ?? ""}
              onChange={e =>
                setTargetLoginId(e.target.value ? Number(e.target.value) : null)
              }>
              <option value="">A new organization sign in</option>
              {existing.map(r => (
                <option key={r.id} value={r.id}>
                  {`${r.name} (${r.organizationName ?? "organization"})`}
                </option>
              ))}
            </select>
          </div>
        )}
        {targetLoginId === null && (
          <>
            <div className="flex flex-col">
              <label htmlFor="merge-org">Organization</label>
              <select
                id="merge-org"
                className={field}
                value={organizationId}
                onChange={e => setOrganizationId(e.target.value)}>
                <option value="">Choose an organization</option>
                {organizations.map(o => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col">
              <label htmlFor="merge-name">Name</label>
              <input
                id="merge-name"
                className={field}
                value={name}
                onChange={e => setName(e.target.value)}
                maxLength={200}
              />
            </div>
            <fieldset className="flex flex-col gap-1">
              <legend>Which stored password or key to keep</legend>
              {sources.map(r => (
                <label key={r.id} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="template"
                    checked={template === r.id}
                    onChange={() => setTemplateId(r.id)}
                  />
                  {keepLabel(r)}
                </label>
              ))}
            </fieldset>
          </>
        )}
        <p className="text-gray-600">
          Every import using these sign ins is moved. Each import owner must be
          a member of the organization.
        </p>
        {refusal && (
          <p role="alert" className="rounded-md bg-red-50 p-3 text-red-700">
            {refusal}
          </p>
        )}
        {done && (
          <p role="status" className="text-green-700">
            {done}
          </p>
        )}
      </div>
    </Modal>
  );
};

export default MergeModal;
